import React, { useRef, useState } from 'react';
import { Table as ReactTable } from 'reactstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toStr, capitalize } from '../misc/utils';
import LinkButton from './LinkButton';
import EmptyTableMessage from './EmptyTableMessage';

export interface SortData {
  sortDescending: boolean;
  sortField: string;
}

export interface Cell<T> {
  name?: Extract<keyof T, string>;
  value?: (obj: T) => string | number | null;
  column?: string;
  render?: (value: string, object: T, tdId: string) => React.JSX.Element;
  css?: string;
  disable?: () => boolean;
  tPrefix?: string;
  sortDisabled?: boolean;
  dependsOn?: Extract<keyof T, string> | null;
  alternativeText?: string;
}

interface Props<T> {
  data: T[];
  cells: Cell<T>[];
  keyFunc: (obj: T) => number;
  hide?: string[];
  sortData?: SortData;
  changeSort?: (field: string, sortDesc: boolean) => void;
  expandable?: boolean;
  onRowClick?: (obj: T) => void;
  discapitalize?: boolean;
  wrapperClass?: string;
  emptyNoDesc?: boolean;
}

const ARROW_DOWN = 'arrow-down';
const ARROW_UP = 'arrow-up';

const Table = <T extends object>(props: Props<T>) => {
  const {
    data,
    cells,
    keyFunc,
    hide,
    sortData,
    changeSort,
    expandable = false,
    onRowClick,
    discapitalize,
    wrapperClass,
    emptyNoDesc,
  } = props;
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [expandedId, setExpandedId] = useState(0);

  const sortable = sortData !== undefined && changeSort !== undefined;
  const dependsCells = [...cells];
  const visibleCells = cells.filter(
    (x) =>
      (!x.dependsOn && (!hide || !x.name || hide.indexOf(x.name) === -1)) ||
      (x.dependsOn &&
        dependsCells.some(
          (d) => d.name === x.dependsOn && (!hide || hide.indexOf(d.name || '') === -1)
        ))
  );

  const drawHeader = (cell: Cell<T>, i: number) => {
    if (sortData && !cell.sortDisabled && changeSort && cell.name) {
      const cellField = !discapitalize ? capitalize(cell.name) : cell.name;
      const sortDesc = sortData.sortField === cellField ? sortData.sortDescending : null;
      const className = classnames(cell.css, 'sortable', { current: sortDesc !== null });
      const sortNext = sortDesc === null ? false : !sortDesc;

      return (
        <th
          id={`col-${cell.name}-${i}`}
          key={`th-${cell.name}-${i}`}
          className={className}
          onClick={() => changeSort(cellField, sortNext)}
        >
          <span>
            {t(cell.column || cell.name)}
            {sortDesc !== null && <FontAwesomeIcon icon={sortDesc ? ARROW_DOWN : ARROW_UP} />}
          </span>
        </th>
      );
    }

    const columnName = cell.name ? cell.name.toString() : i;
    const className = classnames(cell.css, 'noSortable');
    return (
      <th id={`col-${columnName}-${i}`} key={`th-${columnName}-${i}`} className={className}>
        <span>{t(cell.column || cell.name || '')}</span>
      </th>
    );
  };

  const drawCell = (element: T, cell: Cell<T>, rowId: number, i: number, shrink: boolean) => {
    const value = cell.value ? toStr(cell.value(element)) : '';
    const idName = `td-${cell.name ? cell.name.toString() : i}-${i}-${rowId}`;
    const jsx = cell.render && value !== null ? cell.render(value, element, idName) : value;

    return (
      <td id={idName} className={cell.css} key={idName}>
        <div className={shrink && cell.value ? 'shrunken' : ''}>{jsx}</div>
      </td>
    );
  };

  const drawRow = (elem: T) => {
    const rowId = keyFunc(elem);
    let expandCell = null;
    let isExpanded = true;
    if (expandable) {
      isExpanded = expandedId === rowId;
      const icon = isExpanded ? 'chevron-down' : 'chevron-right';
      const toggleExpand = () => setExpandedId(isExpanded ? 0 : rowId);
      expandCell = (
        <td>
          <div>
            <LinkButton htmlId={`exp${rowId}`} icon={icon} onClick={toggleExpand} />
          </div>
        </td>
      );
    }

    const row = (
      <tr key={rowId} onClick={() => onRowClick && onRowClick(elem)}>
        {expandCell}
        {visibleCells.map((cell, i) => drawCell(elem, cell, rowId, i, !isExpanded))}
      </tr>
    );
    return row;
  };

  const tableClassNames = classnames('table--bordered', 'table-hover', 'table-header-sticky', {
    sortable,
  });

  return (
    <>
      <div
        ref={wrapperRef}
        className={`table-wrapper sticky-section${wrapperClass ? ` ${wrapperClass}` : ''}`}
      >
        <ReactTable responsive className={tableClassNames} innerRef={tableRef}>
          <thead>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- espaciador intencional */}
              {expandable && <th> </th>}
              {visibleCells.map((cell, i) => drawHeader(cell, i))}
            </tr>
          </thead>

          <tbody>{data.map((element) => drawRow(element))}</tbody>
        </ReactTable>
      </div>
      {data && data.length === 0 && <EmptyTableMessage noDesc={emptyNoDesc} />}
    </>
  );
};

export default Table;
